import React, { useContext } from 'react';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import OperationContext from '../context';
import { OpMember } from '../../../../services/types/Member';
import userService from '../../../../services/userService';
import { AutocompleteChips, CustomDropDown, Modal } from '../../../../shared/components';
import FormButtons from '../../../../shared/components/form/FormButtons';
import TextInput from '../../../../shared/components/form/TextInput';
import useServiceReducer from '../../../../shared/hooks/useServiceReducer';
import { safeFind } from '../../../../shared/misc/utils';
import reducer, { SearchUserAction } from './addMemberReducer';
import DropdownChecks from '../../../../shared/components/DropdownChecks';

interface FormState {
  member: OpMember | null;
  rationales: string[];
  description: string | null;
}

interface Props {
  visible: boolean;
  setVisible: (value: boolean) => void;
  onSubmit: (member: OpMember | null | undefined) => void;
  onList: OpMember[];
}

export const buildAddMemberSchema = (
  t: (key: string) => string,
  onList: OpMember[],
  rationales: { id: number; value: string; text: string }[],
  otherRationaleValue: string
) =>
  Yup.object().shape({
    member: Yup.object()
      .shape({
        id: Yup.number().test({
          test: (value) => {
            // value indefinido: mismo resultado que el chequeo numérico
            // original hacía por coerción implícita (undefined >= 0 y
            // undefined < 0 son ambos false en JS) - test inválido.
            if (value === undefined) {
              return false;
            }
            return (
              (value >= 0 && !onList.some((x) => !x.id || x.id < 0)) ||
              (value < 0 && !onList.some((x) => x))
            );
          },
          message: (x) =>
            x.value >= 0 ? t('validateMembersHasNotGSRole') : t('validateMembersGSRoleOnly'),
        }),
      })
      .required(t('required'))
      .nullable(),

    rationales: Yup.array()
      .test(
        'rationale-check',
        'Please select ESG Guidance Service support for preparation and/or for supervision',
        // rationales siempre es un array real acá (initialValues lo fija en
        // ['none'], nunca undefined) - la definición de tipos de Yup 1.x
        // ahora lo marca opcional igual, sin cambiar el comportamiento real.
        (value) =>
          (rationales.find((x) => x.id > 100) &&
            (value!.includes('101') || value!.includes('102'))) ||
          !rationales.find((x) => x.id > 100)
      )
      .min(2, t('required')),
    description: Yup.string()
      .nullable()
      .when('rationales', {
        is: (rats: string[]) => rats.includes(otherRationaleValue),
        then: (schema) =>
          schema.concat(Yup.string().required(t('required for "Other" rationale option'))),
      })
      .max(250),
  });

const AddMemberPopup: React.FC<Props> = (props) => {
  const { visible, setVisible, onSubmit, onList } = props;
  const { t } = useTranslation();
  const {
    operation: { memberRationales },
  } = useContext(OperationContext);
  const [members, loadingMembers, , dispatch] = useServiceReducer(userService.getMembers, reducer, {
    query: '',
  });

  const handleMembersSearch = (egsMemberQuery: string) => {
    const action = {
      type: 'QUERY_MEMBER',
      query: egsMemberQuery,
    } as SearchUserAction;
    dispatch(action);
  };

  const filteredMembers = members
    ? members.filter((x) => !onList.some((l) => x.id === l.id))
    : null;
  const rationales = memberRationales.map((x) => ({
    value: x.id.toString(),
    id: x.id,
    text: x.name,
  }));

  const otherRationaleValue = safeFind(rationales, (x) => x.text === 'Other').value;

  const rationalesGS = [
    { value: '101', id: 100, text: 'ESG Guidance Service: Support to preparation' },
    { value: '102', id: 101, text: 'ESG Guidance Service: Support to execution' },
  ];

  const preSubmit = (values: FormState) => {
    if (values.member && values.rationales.length > 1) {
      values.rationales.push(
        values.rationales.splice(values.rationales.indexOf(otherRationaleValue), 1)[0]
      );
      const rationaleTexts = values.rationales
        .filter((x) => x !== 'none')
        .map((r) => {
          const rat = safeFind(rationales, (x) => x.value === r);
          return rat.text === 'Other' ? `${rat.text}: ${values.description}` : rat.text;
        });

      const member = { ...values.member };
      member.rationale = rationaleTexts.join(', ');

      member.isPrimary = onList.length === 0;
      setVisible(false);
      onSubmit(member);
    }
  };

  const setRationaleSelected = (id: string, checked: boolean, formik: FormikProps<FormState>) => {
    let newValues;
    if (!checked) {
      newValues = [...formik.values.rationales].filter((x) => x !== id);
    } else {
      newValues = [...formik.values.rationales, id];
    }
    formik.setFieldValue('rationales', newValues);
  };

  const updateRationales = (member: OpMember | null) => {
    if (member === null) {
      if (rationales.find((x) => x.id > 100)) {
        rationales.pop();
        rationales.pop();
      }
      return;
    }

    if (member !== undefined && Number(member.id) === -1) {
      if (!rationales.find((x) => x.id > 100)) {
        rationales.push(rationalesGS[0]);
        rationales.push(rationalesGS[1]);
      }
    }
  };

  return (
    <Modal title={t('addESGMember')} visible={visible} setVisible={() => setVisible(false)}>
      <Formik<FormState>
        initialValues={{ member: null, rationales: ['none'], description: null }}
        onSubmit={(values) => preSubmit(values)}
        validationSchema={buildAddMemberSchema(t, onList, rationales, otherRationaleValue)}
        enableReinitialize
      >
        {(formik) => {
          const { setValues } = formik;
          return (
            <div className="px-4">
              <form className="form" onSubmit={formik.handleSubmit}>
                <div className="form__form-group">
                  <AutocompleteChips<OpMember>
                    text={t('member')}
                    labelKey="fullName"
                    htmlId="egs-members-typeahead"
                    list={filteredMembers}
                    placeholder="Ex. Matt Damon"
                    onSearch={handleMembersSearch}
                    onChange={(value) => {
                      setValues({ ...formik.values, member: value[0] });
                      updateRationales(value[0]);
                    }}
                    loading={loadingMembers}
                    render={(member: OpMember) => (
                      <div>
                        {member.fullName}{' '}
                        {member.group && (
                          <span className="ml-1" style={{ color: '#c4c4c4', fontSize: '12px' }}>
                            Group {member.group}
                          </span>
                        )}
                      </div>
                    )}
                    className="mb-1"
                  />
                  {formik.touched.member && formik.errors.member && (
                    <span className="form__form-group-error mt-1">
                      {typeof formik.errors.member === 'object'
                        ? // eslint-disable-next-line dot-notation
                          formik.errors.member['id']
                        : formik.errors.member}
                    </span>
                  )}
                </div>
                <div className="form__form-group">
                  <span>{t('rationalJustification')}</span>
                  <CustomDropDown
                    text={
                      formik.values.rationales.length > 1
                        ? `${formik.values.rationales.length - 1} ${t('selected')}`
                        : t('selectTheOptions')
                    }
                    className="custom-filters-dropdown mt-1"
                  >
                    <DropdownChecks
                      elements={rationales.map((x) => ({ id: x.value, name: x.text }))}
                      selected={formik.values.rationales}
                      setSelected={(id, checked) => {
                        setRationaleSelected(id, checked, formik);
                      }}
                      translate={false}
                    />
                  </CustomDropDown>
                  {formik.touched.rationales && formik.errors.rationales && (
                    <span className="form__form-group-error mt-1">{formik.errors.rationales}</span>
                  )}
                </div>
                <TextInput
                  {...formik}
                  name="description"
                  label={t('description')}
                  type="textarea"
                  disabled={!formik.values.rationales.includes(otherRationaleValue)}
                />
                <FormButtons
                  onCancel={() => {
                    setVisible(false);
                  }}
                  submitText={t('addESGMember')}
                  noSpinner
                  className="pr-0"
                />
              </form>
            </div>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddMemberPopup;
